import { useState, useEffect } from "react";
import _, { isEqual } from "lodash";
import {
  getOrgRoles,
  addRole,
  updateRole,
  deleteRole,
  uploadFileToAWS,
  getUploadingFileData
} from "../../../service";
import { PlusIcon, RemoveIcon } from "../../../asssets/icons";
import { ImageUploader } from "../../common";
import { Spinner } from "../../form";
import { TrashIcon } from "@heroicons/react/24/outline";

const AddOrgRoles = ({ org, orgId }) => {
  const [originalRoles, setOriginalRoles] = useState([]);
  const [roleSection, setRoleSection] = useState([
    {
      _id: "",
      name: "",
      name_fr: "",
      name_en: "",
      name_de: "",
      name_it: "",
      logo: "",
      logo_obj: null,
      image_fr: "",
      image_en: "",
      image_de: "",
      image_it: "",
      image_fr_obj: null,
      image_en_obj: null,
      image_de_obj: null,
      image_it_obj: null,
      cloud_fr: "",
      cloud_en: "",
      cloud_de: "",
      cloud_it: "",
      cloud_fr_obj: null,
      cloud_en_obj: null,
      cloud_de_obj: null,
      cloud_it_obj: null,
      description_fr: "",
      description_en: "",
      description_de: "",
      description_it: "",
      vip_id_fr: "",
      vip_id_en: "",
      vip_id_de: "",
      vip_id_it: "",
      alternate_vip_ids: {
        en: [],
        de: [],
        fr: [],
        it: []
      }
    }
  ]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchRoles = async () => {
    try {
      const { data } = await getOrgRoles(orgId);
      data.length > 0 ? setRoleSection(data) : addRoleSection();
      setOriginalRoles(data);
    } catch (error) {
      setOriginalRoles([]);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [org, orgId]);

  const handleChange = (index, path, value) => {
    const updatedRoles = _.cloneDeep([...roleSection]);
    updatedRoles[index] = _.set(updatedRoles[index], path, value);
    setRoleSection(updatedRoles);
  };

  const handleUploadChange = (roleId, file, index, key_name, obj_name) => {
    if (file !== undefined && file !== null && file.name !== undefined && file.name !== null) {
      const filename = `roles-images/${orgId}${
        process.env.REACT_APP_BASE_URL?.includes("dev") ? "/dev" : ""
      }/${roleId}_${key_name}`;
      const updatedSections = [...roleSection];
      updatedSections[index] = {
        ...updatedSections[index],
        [obj_name]: file,
        [key_name]: filename
      };

      setRoleSection(updatedSections);
    }
  };

  const uploadImage = async (image, filename) => {
    if (image !== undefined) {
      const {
        data: { url }
      } = await getUploadingFileData({
        name: filename,
        type: image.type
      });
      await uploadFileToAWS(url, image, image.type, (progress) => {
        setUploadProgress({ ...uploadProgress, [filename]: progress?.progress });
      });
    }
  };

  const saveRoles = async () => {
    setSaving(true);
    for (const [index, item] of roleSection.entries()) {
      // Upload per-language images
      if (item?.image_en_obj || item?.image_en_obj?.name !== undefined) {
        setSaving(`Uploading EN image #${index + 1}...`);
        await uploadImage(item.image_en_obj, item.image_en);
      }
      if (item?.image_de_obj || item?.image_de_obj?.name !== undefined) {
        setSaving(`Uploading DE image #${index + 1}...`);
        await uploadImage(item.image_de_obj, item.image_de);
      }
      if (item?.image_fr_obj || item?.image_fr_obj?.name !== undefined) {
        setSaving(`Uploading FR image #${index + 1}...`);
        await uploadImage(item.image_fr_obj, item.image_fr);
      }
      if (item?.image_it_obj || item?.image_it_obj?.name !== undefined) {
        setSaving(`Uploading IT image #${index + 1}...`);
        await uploadImage(item.image_it_obj, item.image_it);
      }

      // Upload per-language cloud images
      if (item?.cloud_en_obj || item?.cloud_en_obj?.name !== undefined) {
        setSaving(`Uploading EN cloud #${index + 1}...`);
        await uploadImage(item.cloud_en_obj, item.cloud_en);
      }
      if (item?.cloud_de_obj || item?.cloud_de_obj?.name !== undefined) {
        setSaving(`Uploading DE cloud #${index + 1}...`);
        await uploadImage(item.cloud_de_obj, item.cloud_de);
      }
      if (item?.cloud_fr_obj || item?.cloud_fr_obj?.name !== undefined) {
        setSaving(`Uploading FR cloud #${index + 1}...`);
        await uploadImage(item.cloud_fr_obj, item.cloud_fr);
      }
      if (item?.cloud_it_obj || item?.cloud_it_obj?.name !== undefined) {
        setSaving(`Uploading IT cloud #${index + 1}...`);
        await uploadImage(item.cloud_it_obj, item.cloud_it);
      }

      // Upload logo
      if (item?.logo_obj || item?.logo_obj?.name !== undefined) {
        setSaving(`Uploading logo #${index + 1}...`);
        await uploadImage(item.logo_obj, item.logo);
      }

      if (item._id !== "") {
        if (!isEqual(item, originalRoles[index])) {
          setSaving(`Updating role #${index + 1}...`);
          await updateRole(orgId, item);
        }
      } else {
        setSaving(`Adding role #${index + 1}...`);
        await addRole(orgId, item);
      }
    }

    setSaving("Roles saved successfully");
    fetchRoles();
    setTimeout(() => {
      setSaving(false);
    }, 1000);
  };

  const addRoleSection = () => {
    setRoleSection([
      ...roleSection,
      {
        _id: "",
        name: "",
        name_fr: "",
        name_en: "",
        name_de: "",
        name_it: "",
        logo: "",
        logo_obj: null,
        image_fr: "",
        image_en: "",
        image_de: "",
        image_it: "",
        image_fr_obj: null,
        image_en_obj: null,
        image_de_obj: null,
        image_it_obj: null,
        cloud_fr: "",
        cloud_en: "",
        cloud_de: "",
        cloud_it: "",
        cloud_fr_obj: null,
        cloud_en_obj: null,
        cloud_de_obj: null,
        cloud_it_obj: null,
        description_fr: "",
        description_en: "",
        description_de: "",
        description_it: "",
        short_description_fr: "",
        short_description_en: "",
        short_description_de: "",
        short_description_it: "",
        vip_id_fr: "",
        vip_id_en: "",
        vip_id_de: "",
        vip_id_it: ""
      }
    ]);
  };

  const removeRoleSection = async (index) => {
    const updatedSections = [...roleSection];
    const removedSection = updatedSections[index];
    if (removedSection._id !== "") await deleteRole(orgId, { _id: removedSection._id });
    updatedSections.splice(index, 1);
    setRoleSection(updatedSections);
  };

  return (
    <>
      <div className="container mx-auto p-4">
        <div className="flex justify-end mt-4 fixed top-4 right-4 z-[999999]">
          <button
            onClick={saveRoles}
            type="button"
            disabled={saving}
            className="flex flex-col gap-2 disabled:opacity-60 select-none py-2 font-bold text-md border bg-[#515151] text-[#FFFFFF] px-8 rounded-3xl">
            <span className="flex items-center gap-2">
              Save Roles {saving && <Spinner isSubmitting={saving} size={16} color="#FFFFFF" />}
            </span>
            {saving && (
              <span className="block">
                <span className="text-xs text-gray-300">
                  {typeof saving === "string" ? saving : "Saving..."}
                </span>
              </span>
            )}
          </button>
        </div>
      </div>
      <div className="space-y-8">
        {roleSection.map((role, index) => (
          <div key={index} className="container mx-auto">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Role #{index + 1}</h3>
                {index > 0 && (
                  <button
                    className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    onClick={() => removeRoleSection(index)}>
                    <RemoveIcon />
                  </button>
                )}
              </div>

              {/* Images Section - 3 Column Grid */}
              <div className="mb-8">
                <h4 className="text-md font-semibold text-gray-700 mb-4">Images</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Logo */}
                  <div className="grid grid-cols-1 gap-2">
                    <ImageUploader
                      label="Logo"
                      image={role.logo_obj || (role.logo ? role.logo : null)}
                      setImage={(image) => {
                        handleUploadChange(role._id, image, index, "logo", "logo_obj");
                      }}
                      uploadProgress={uploadProgress[role.logo] || 0}
                    />
                  </div>

                  {/* Per-Language Cloud Images */}
                  {org.languages?.map((language, languageIndex) => (
                    <div key={languageIndex} className="grid grid-cols-1 gap-2">
                      <ImageUploader
                        label={`Image ${language.toUpperCase()}`}
                        image={
                          role[`image_${language}_obj`] ||
                          (role[`image_${language}`] ? role[`image_${language}`] : null)
                        }
                        setImage={(image) => {
                          handleUploadChange(
                            role._id,
                            image,
                            index,
                            `image_${language}`,
                            `image_${language}_obj`
                          );
                        }}
                        uploadProgress={uploadProgress[role?.[`image_${language}`]] || 0}
                      />
                    </div>
                  ))}

                  {/* Cloud Images for each language */}
                  {org.languages?.map((language, languageIndex) => (
                    <div key={`cloud_${languageIndex}`}>
                      <ImageUploader
                        label={`Cloud ${language.toUpperCase()}`}
                        image={
                          role[`cloud_${language}_obj`] ||
                          (role[`cloud_${language}`] ? role[`cloud_${language}`] : null)
                        }
                        setImage={(image) => {
                          handleUploadChange(
                            role._id,
                            image,
                            index,
                            `cloud_${language}`,
                            `cloud_${language}_obj`
                          );
                        }}
                        uploadProgress={uploadProgress[role?.[`cloud_${language}`]] || 0}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Text Fields Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-700 mb-4">Text Content</h4>
                <div className={`grid grid-cols-${org.languages?.length} gap-2`}>
                  {org.languages?.map((language, languageIndex) => (
                    <div key={languageIndex} className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wide border-b border-gray-300 pb-2">
                        {language}
                      </h5>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Role Name
                        </label>
                        <input
                          type="text"
                          name={`name_${language}`}
                          value={role[`name_${language}`] || ""}
                          onChange={(e) => handleChange(index, `name_${language}`, e.target.value)}
                          placeholder={`Enter name in ${language.toUpperCase()}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-white text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Description
                        </label>
                        <textarea
                          name={`description_${language}`}
                          value={role[`description_${language}`] || ""}
                          onChange={(e) =>
                            handleChange(index, `description_${language}`, e.target.value)
                          }
                          placeholder={`Enter description in ${language.toUpperCase()}`}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-white text-sm focus:border-black focus:ring-0 focus:outline-none resize-none transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Short description
                        </label>
                        <textarea
                          name={`short_description_${language}`}
                          value={role[`short_description_${language}`] || ""}
                          onChange={(e) =>
                            handleChange(index, `short_description_${language}`, e.target.value)
                          }
                          placeholder={`Enter description in ${language.toUpperCase()}`}
                          rows={1}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-white text-sm focus:border-black focus:ring-0 focus:outline-none resize-none transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          VIP ID {language.toUpperCase()}
                        </label>
                        <input
                          type="text"
                          name={`vip_id_${language}`}
                          value={role[`vip_id_${language}`] || ""}
                          onChange={(e) =>
                            handleChange(index, `vip_id_${language}`, Number(e.target.value))
                          }
                          placeholder={`Enter VIP ID in ${language.toUpperCase()}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-white text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                        />

                        <div className="flex flex-col gap-2 my-2">
                          <p className="block text-xs font-medium text-gray-600">
                            Alternate VIP IDs in {language.toUpperCase()}
                          </p>
                          <div className="flex flex-row flex-wrap gap-2">
                            {role?.alternate_vip_ids?.[language] &&
                              role?.alternate_vip_ids?.[language]?.map((aVipId, aVipIndex) => {
                                return (
                                  <div
                                    key={`alternate_vip_id_${language}_${aVipIndex}`}
                                    className="flex gap-2 items-center border border-gray-300 rounded-md px-2 py-1">
                                    <input
                                      className="w-20 px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                                      type="text"
                                      name={`alternate_vip_id_${language}`}
                                      value={aVipId}
                                      onChange={(e) =>
                                        handleChange(
                                          index,
                                          `alternate_vip_ids.${language}.[${aVipIndex}]`,
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                    <button
                                      onClick={() => {
                                        const updatedRole = [
                                          ...role?.alternate_vip_ids?.[language]
                                        ]?.filter((id) => Number(id) !== Number(aVipId));

                                        handleChange(
                                          index,
                                          `alternate_vip_ids.${language}`,
                                          updatedRole
                                        );
                                      }}>
                                      <TrashIcon className="w-4 h-4 text-red-500" />
                                    </button>
                                  </div>
                                );
                              })}
                            <button
                              className="flex items-center gap-2 border border-gray-300 rounded-md px-2 py-1"
                              onClick={() => {
                                handleChange(
                                  index,
                                  `alternate_vip_ids.${language}.[${
                                    role?.alternate_vip_ids?.[language]?.length || 0
                                  }]`,
                                  ""
                                );
                              }}>
                              <span className="w-4 h-4">
                                <PlusIcon />
                              </span>
                              <span className="text-xs">Add</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 w-full">
        <div className="flex justify-end mt-8 w-full">
          <button
            className="flex items-center gap-2 px-6 py-3 bg-blue-200 text-[#606060] text-xl font-bold rounded-lg hover:bg-blue-400 transition-colors shadow-sm"
            onClick={() => addRoleSection()}>
            <span className="w-8 h-8">
              <PlusIcon />
            </span>
            <span>Add New Role</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AddOrgRoles;
